'use client';

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

export const SupportClock = () => {
    const [currentHour, setCurrentHour] = React.useState(new Date().getHours());
    const [isHovered, setIsHovered] = React.useState(false);

    React.useEffect(() => {
        const timer = setInterval(() => {
            setCurrentHour(new Date().getHours());
        }, 60000); // Update every minute

        return () => clearInterval(timer);
    }, []);

    const radius = 16;
    const center = radius;
    const strokeWidth = 0.5;
    const toFixed = (num: number, digits = 4) => +num.toFixed(digits);

    const hourMarks = useMemo(() => {
        return Array.from({ length: 24 }, (_, i) => {
            const angle = (i * 360) / 24;
            const radians = (angle - 90) * (Math.PI / 180);
            const x1 = toFixed(center + (radius - 2) * Math.cos(radians));
            const y1 = toFixed(center + (radius - 2) * Math.sin(radians));
            const x2 = toFixed(center + radius * Math.cos(radians));
            const y2 = toFixed(center + radius * Math.sin(radians));

            return { x1, y1, x2, y2 };
        });
    }, [center, radius]);

    const startAngle = ((currentHour - 1) * 360) / 24;
    const endAngle = (currentHour * 360) / 24;
    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);
    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    return (
        <motion.div
            className="relative group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <svg width="32" height="32" viewBox="0 0 32 32" className="opacity-90">
                <defs>
                    <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="1" result="coloredBlur" />
                        <feFlood floodColor="#22C55E" floodOpacity="0.5" result="glowColor" />
                        <feComposite
                            in="glowColor"
                            in2="coloredBlur"
                            operator="in"
                            result="softGlow"
                        />
                        <feMerge>
                            <feMergeNode in="softGlow" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    <filter id="neonGlowIntense" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                        <feFlood floodColor="#22C55E" floodOpacity="0.8" result="glowColor" />
                        <feComposite
                            in="glowColor"
                            in2="coloredBlur"
                            operator="in"
                            result="softGlow"
                        />
                        <feMerge>
                            <feMergeNode in="softGlow" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke="#22C55E"
                    strokeWidth={strokeWidth}
                    opacity={0.8}
                    filter={isHovered ? 'url(#neonGlowIntense)' : 'url(#neonGlow)'}
                />

                {hourMarks.map((mark, i) => (
                    <line
                        key={i}
                        x1={mark.x1}
                        y1={mark.y1}
                        x2={mark.x2}
                        y2={mark.y2}
                        stroke="#22C55E"
                        strokeWidth={strokeWidth}
                        opacity={0.6}
                        filter={isHovered ? 'url(#neonGlowIntense)' : 'url(#neonGlow)'}
                    />
                ))}

                <path
                    d={`M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} Z`}
                    fill="#22C55E"
                    opacity={isHovered ? 0.5 : 0.3}
                    filter={isHovered ? 'url(#neonGlowIntense)' : 'url(#neonGlow)'}
                />

                <circle
                    cx={center}
                    cy={center}
                    r={1}
                    fill="#22C55E"
                    filter={isHovered ? 'url(#neonGlowIntense)' : 'url(#neonGlow)'}
                />
            </svg>

            {/* Hover tooltip */}
            <motion.div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap text-xs text-gray-600 pointer-events-none bg-white/80 px-2 py-1 rounded-full shadow-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ duration: 0.2 }}
            >
                Поддержка 24/7
            </motion.div>
        </motion.div>
    );
};
